import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type ListItemVariant = 'base' | 'short';

export interface ListItemProps extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'title' | 'type'
> {
    variant?: ListItemVariant;
    title: ReactNode;
    description?: ReactNode;
    startContent?: ReactNode;
    endContent?: ReactNode;
    titleExtendedContent?: ReactNode;
    selected?: boolean;
    interactive?: boolean;
    type?: 'button' | 'submit' | 'reset';
}

export const ListItem = forwardRef<HTMLButtonElement, ListItemProps>(
    (
        {
            variant = 'base',
            title,
            description,
            startContent,
            endContent,
            titleExtendedContent,
            selected = false,
            disabled = false,
            interactive = true,
            className,
            type = 'button',
            ...props
        },
        ref,
    ) => {
        const isShort = variant === 'short';

        const containerClassName = cn(
            'group flex w-full items-center rounded-lg transition-colors',
            isShort ? 'gap-2 p-2' : 'gap-3 py-2 pl-2 pr-4',
            selected && 'bg-grey-600',
            !selected && !disabled && 'hover:bg-surface-hover',
            disabled && 'cursor-not-allowed opacity-60',
            className,
        );

        const contentClassName = cn(
            'flex min-w-0 flex-1 items-center text-left',
            isShort ? 'gap-2' : 'gap-3',
        );

        const shortTitleClassName = cn(
            'min-w-0 flex-1 truncate text-left text-sm leading-5',
            selected ? 'text-white-100' : 'text-grey-200',
            disabled && !selected && 'text-grey-300',
        );

        const baseTitleClassName =
            'min-w-0 flex-1 truncate text-left text-sm-medium text-white-100';

        const startContentClassName = cn(
            'flex shrink-0 items-center',
            isShort && '[&>svg]:size-3.5 [&>svg]:shrink-0',
        );

        const mainContent = (
            <>
                {startContent && (
                    <span className={startContentClassName}>
                        {startContent}
                    </span>
                )}
                {isShort ? (
                    <span className={shortTitleClassName}>{title}</span>
                ) : (
                    <span className="flex min-w-0 flex-1 flex-col items-stretch justify-center gap-1">
                        <span className="flex w-full items-center gap-1.5">
                            <span className={baseTitleClassName}>{title}</span>
                            {titleExtendedContent && (
                                <span className="flex shrink-0 items-center">
                                    {titleExtendedContent}
                                </span>
                            )}
                        </span>
                        {description && (
                            <span className="w-full text-left text-xs-medium text-grey-200">
                                {description}
                            </span>
                        )}
                    </span>
                )}
            </>
        );

        const trailing = endContent && (
            <span className="flex shrink-0 items-center">{endContent}</span>
        );

        // endContent is rendered as a sibling of the clickable button rather
        // than a descendant, so interactive endContent (menu triggers, add
        // buttons) doesn't nest a <button> inside a <button>.
        if (!interactive) {
            return (
                <div className={containerClassName}>
                    <span className={contentClassName}>{mainContent}</span>
                    {trailing}
                </div>
            );
        }

        return (
            <div className={containerClassName}>
                <button
                    ref={ref}
                    type={type}
                    disabled={disabled}
                    className={contentClassName}
                    {...props}
                >
                    {mainContent}
                </button>
                {trailing}
            </div>
        );
    },
);

ListItem.displayName = 'ListItem';
